"use client";

import { useState } from "react";
import { useUser } from "@clerk/nextjs";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const ROLES = [
  { id: "patient", label: "Ich lebe mit Diabetes" },
  { id: "typf", label: "Ich bin Typ F (Familie & Freunde)" },
  { id: "professional", label: "Ich bin Fachpersonal" },
  { id: "corporate", label: "Ich bin Mitarbeiter:in eines Unternehmens" },
];

const ProfileSettingsPage = () => {
  const { user } = useUser();
  const [name, setName] = useState(user?.firstName || "");
  const [selectedRole, setSelectedRole] = useState<string>((user?.publicMetadata?.role as string) || "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const originalName = user?.firstName || "";
  const originalRole = (user?.publicMetadata?.role as string) || "";
  const hasChanges = name.trim() !== originalName || selectedRole !== originalRole;

  const handleSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const res = await fetch("/api/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          ...(selectedRole && { role: selectedRole }),
        }),
      });
      if (res.ok) {
        setSuccess(true);
        // Reload Clerk user data so dashboard updates instantly
        await user?.reload();
      } else {
        setError("Fehler beim Speichern");
      }
    } catch {
      setError("Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <label className="text-copy-sm font-bold text-black">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => { setName(e.target.value); setSuccess(false); setError(""); }}
          placeholder="Dein Name"
          className="h-[38px] w-full rounded-full border border-sand-10 bg-white px-5 py-2 text-copy-sm text-black caret-sky-50 placeholder:text-gray-75 focus:outline-none focus:ring-2 focus:ring-sky-50"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-copy-sm font-bold text-black">Rolle</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ROLES.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => { setSelectedRole(role.id); setSuccess(false); setError(""); }}
              className={cn(
                "flex min-h-[72px] w-full items-center justify-center rounded-[24px] bg-white border border-sand-10 px-4 py-3 text-center text-copy-sm text-black transition-all focus:outline-none",
                selectedRole === role.id
                  ? "ring-2 ring-sky-50 bg-sky-10 border-transparent"
                  : "hover:ring-2 hover:ring-sky-50"
              )}
            >
              {role.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-copy-sm text-center" style={{ color: "hsl(var(--red-100))" }}>{error}</p>}
      {success && <p className="text-copy-sm text-center text-sky-100">✓ Gespeichert</p>}

      <Button variant="black" size="sm" onClick={handleSave} disabled={loading || !name.trim() || !hasChanges}>
        {loading ? "Wird gespeichert..." : "Speichern"}
      </Button>
    </div>
  );
};

export { ProfileSettingsPage };
